import { Box, Divider, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router';
import { useListAgentsQuery } from '../../api/agents.api';
import { useGetCatalogQuery, useGetMethodQuery } from '../../api/content.api';
import { AiAugmentation } from '../../components/AiAugmentation';
import { ComingSoonRow } from '../../components/ComingSoonRow';
import { ConditionsCard } from '../../components/ConditionsCard';
import { ContentSection } from '../../components/ContentSection';
import { ListCard } from '../../components/ListCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { MasteryLevels } from '../../components/MasteryLevels';
import { MasteryTargetBadge } from '../../components/MasteryTargetBadge';
import { NearbyMethods } from '../../components/NearbyMethods';
import { StepList } from '../../components/StepList';
import { ViewInConfluence } from '../../components/ViewInConfluence';
import { SDLC_PATH } from '../../config';
import { SECTION_COPY, nearbyMethods } from '../../content';
import { cardSurfaceSx, sectionSpacingSx } from '../../theme';
import { ContentState } from '../../components/ContentState';

/**
 * `/sdlc/:phaseSlug/:processSlug/:methodSlug` — a method, the leaf of the tree.
 *
 * **One request for the content, and the catalog for the neighbours.** A method
 * has no children, so unlike the two levels above there is nothing to fetch
 * alongside it; the only other thing on the page is the row of sibling methods,
 * which comes out of the catalog the phase page has usually already warmed.
 *
 * **Two columns on desktop, one below it.** The left is the reading order —
 * what it is, why, how — and the right is reference material you look across at
 * rather than through. On a narrow screen that distinction cannot survive, so
 * the cards fall in underneath and the prose keeps its order.
 *
 * The page is assembled from two Figma frames: the shape and the Coming Soon
 * and Nearby blocks from the unified L3, the card anatomy and the
 * Confluence-aligned headings from the frame marked `[Archive]`, which is the
 * one the field mapping was made against.
 */
export const MethodPage = () => {
  const { phaseSlug = '', processSlug = '', methodSlug = '' } = useParams();
  const path = `${phaseSlug}/${processSlug}/${methodSlug}`;
  const { data: method, isLoading, error, refetch } = useGetMethodQuery(path);
  const { data: catalog } = useGetCatalogQuery();
  const { data: agents } = useListAgentsQuery();

  // Matched on the method's id, which is the Confluence page id the agent file
  // names and which survives a rename. **A failed or empty list is silent**, as
  // the catalog's is: an error banner over a page that otherwise works is a
  // larger loss than the card it reports.
  const linkedAgents =
    method === undefined
      ? []
      : (agents ?? []).filter((agent) => agent.methods.some((link) => link.id === method.id));

  const nearby = nearbyMethods(catalog ?? [], path);
  const processReference =
    method === undefined
      ? ''
      : `${String(method.phaseNumber)}.${String(method.processNumber)}`;

  return (
    <Box sx={{ paddingBottom: 8 }}>
      <Box sx={{ marginTop: 8 }}>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load this method"
          errorNoun="method"
          skeleton={<LoadingSkeleton layout="method" label="Loading the method" />}
        >
          {method !== undefined && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 4,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    sx={{ color: 'text.secondary', letterSpacing: '1px' }}
                  >
                    Method {processReference}.{method.methodNumber} &middot; Level 3 Method
                  </Typography>

                  {method.content.masteryTargetText !== undefined && (
                    <MasteryTargetBadge
                      target={method.content.masteryTargetText}
                      stage={method.stage}
                    />
                  )}
                </Box>

                <Typography variant="h1" component="h1">
                  {method.method}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 4,
                    rowGap: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Parent process:{' '}
                    {/* Blue in the designs, so it is a real link: the process
                        is one segment up and the page already knows the path. */}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'text.link', '&:hover': { textDecoration: 'underline' } }}
                    >
                      <RouterLink
                        to={`${SDLC_PATH}/${phaseSlug}/${processSlug}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {method.content.parentProcessText}
                      </RouterLink>
                    </Typography>
                  </Typography>

                  {/* The pipe is the component's, and hidden: it separates two
                      facts visually and says nothing when read aloud. */}
                  <Typography
                    component="span"
                    variant="body2"
                    aria-hidden
                    sx={{ color: 'text.disabled' }}
                  >
                    |
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Owner: {method.content.ownerText}
                  </Typography>

                  <Box sx={{ flex: 1 }} />

                  <ViewInConfluence confluencePageId={method.confluencePageId} />
                </Box>
              </Box>

              <Box
                sx={{
                  ...sectionSpacingSx,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 936fr) minmax(0, 400fr)' },
                  columnGap: 8,
                  rowGap: 6,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {method.content.descriptionText !== '' && (
                    <ContentSection
                      heading={SECTION_COPY.description.heading}
                      caption={SECTION_COPY.description.caption}
                    >
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {method.content.descriptionText}
                      </Typography>
                    </ContentSection>
                  )}

                  {method.content.whyText !== undefined && (
                    <ContentSection
                      heading={SECTION_COPY.why.heading}
                      caption={SECTION_COPY.why.caption}
                    >
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {method.content.whyText}
                      </Typography>
                    </ContentSection>
                  )}

                  {method.content.howStepByStepList !== undefined && (
                    <ContentSection
                      heading={SECTION_COPY.howSteps.heading}
                      caption={SECTION_COPY.howSteps.caption}
                    >
                      <StepList steps={method.content.howStepByStepList} />
                    </ContentSection>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {method.content.masteryLevelsList !== undefined && (
                    <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
                      <ContentSection
                        heading={SECTION_COPY.masteryLevels.heading}
                        caption={SECTION_COPY.masteryLevels.caption}
                        level={3}
                      >
                        <MasteryLevels
                          levels={method.content.masteryLevelsList}
                          target={method.content.masteryTargetText}
                        />
                      </ContentSection>
                    </Box>
                  )}

                  <ListCard
                    heading={SECTION_COPY.whatYouDeliver.heading}
                    caption={SECTION_COPY.whatYouDeliver.caption}
                    items={method.content.whatList ?? []}
                  />

                  <ConditionsCard
                    preConditions={method.content.inputsAndPreConditionsList}
                    postConditions={method.content.outputsAndPostConditionsList}
                  />

                  <ListCard
                    heading="Controls + Support"
                    caption="Compliance, policies, and frameworks"
                    items={method.content.controlsAndSupportList ?? []}
                  />
                </Box>
              </Box>

              <Divider sx={sectionSpacingSx} />

              <Box sx={sectionSpacingSx}>
                <ContentSection heading={SECTION_COPY.aiAugmentation.heading}>
                  <AiAugmentation
                    practices={method.content.aiPracticesList}
                    tools={method.content.aiToolList}
                    waysOfWorking={method.content.aiWaysOfWorkingList}
                    agents={linkedAgents}
                  />
                </ContentSection>
              </Box>

              <Divider sx={sectionSpacingSx} />

              <Box sx={sectionSpacingSx}>
                <ContentSection heading={SECTION_COPY.comingSoon.heading}>
                  <ComingSoonRow />
                </ContentSection>
              </Box>

              {nearby.length > 0 && (
                <>
                  <Divider sx={sectionSpacingSx} />

                  <Box sx={sectionSpacingSx}>
                    <NearbyMethods
                      methods={nearby}
                      processLabel={method.process}
                      processReference={processReference}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </ContentState>
      </Box>
    </Box>
  );
};
